import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

const authApiInstance = axios.create({
  baseURL: '/auth',
  withCredentials: true,
  timeout: 15000,
})

const adminApiInstance = axios.create({
  baseURL: '/admin',
  withCredentials: true,
  timeout: 15000,
})

export function normalizeError(error) {
  if (error.response) {
    const { status } = error.response
    const data = error.response.data
    return { status, message: data?.error || data?.message || error.message || 'An error occurred', details: data, isNetworkError: false }
  } else if (error.request) {
    return { status: null, message: 'Network error', details: {}, isNetworkError: true }
  }
  return { status: null, message: error.message || 'Unknown error', details: {}, isNetworkError: false }
}

// Notified when the session turns out to be dead. AuthContext registers here
// and clears the user, which lets ProtectedRoute do the redirect to /login.
// Deliberately not a hard window.location redirect: the public review pages
// render fine signed-out, and bouncing a visitor off a shared link would be
// worse than showing them the logged-out view.
let onUnauthorized = null

export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn
}

api.interceptors.request.use((config) => config, (error) => Promise.reject(normalizeError(error)))
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const normalized = normalizeError(error)
    if (normalized.status === 401) {
      console.warn('[401] Session may have expired')
      // Only this instance carries the interceptor — the admin and auth
      // instances are separate, so an admin-only 401 can't sign a user out.
      if (onUnauthorized) onUnauthorized()
    } else if (normalized.status === 403) console.warn('[403] Insufficient permissions')
    else if (normalized.status === 500) console.error('[500] Server error')
    return Promise.reject(normalized)
  },
)

/** Authentication API */
export const authApi = {
  checkAuth: () => authApiInstance.get('/check-auth'),
  logout: () => authApiInstance.get('/logout'),
  callback: (data) => authApiInstance.post('/callback', data),
  checkPin: () => authApiInstance.get('/check-pin'),
  getProviders: () => authApiInstance.get('/providers'),
  jellyfinLogin: (username, password) => authApiInstance.post('/jellyfin/login', { username, password }),
}

/** Recommendations */
export const recommendationsApi = {
  getRecommendations: () => api.get('/recommendations'),
}

/** Popular content (Tautulli home stats) */
export const popularApi = {
  getPopular: () => api.get('/popular'),
}

/** Discover / Library Browse */
export const discoverApi = {
  getDiscover: (params) => api.get('/discover', { params }),
  getGenres: () => api.get('/discover/genres'),
  getFacets: (field, q = '', limit) => api.get('/discover/facets', { params: { field, q, limit } }),
}

/** Search */
export const searchApi = {
  search: (query, page = 1, genre, type, filters = {}) => api.get('/search', {
    params: {
      q: query,
      page,
      genre,
      type,
      filterGenres: filters.filterGenres?.length ? filters.filterGenres.join(',') : undefined,
      contentRatings: filters.contentRatings?.length ? filters.contentRatings.join(',') : undefined,
      yearFrom: filters.yearFrom || undefined,
      yearTo: filters.yearTo || undefined,
      minScore: filters.minScore || undefined,
    },
  }),
  getSuggestions: (query) => api.get('/search/suggest', { params: { q: query } }),
  getDetails: (tmdbId, type) => api.get('/search/details', { params: { tmdbId, type } }),
  // ratingKey pins the library copy so the response can flag seasons already
  // complete in the library (and ones already requested) for graying out.
  getSeasons: (tmdbId, ratingKey) => api.get('/search/seasons', { params: { tmdbId, ratingKey: ratingKey || undefined } }),
  getSimilar: (tmdbId, type, hideLibrary) => api.get('/search/similar', { params: { tmdbId, type, hideLibrary } }),
  getPersonCredits: (personId, hideLibrary) => api.get('/search/person', { params: { personId, hideLibrary } }),
}

/** Watchlist */
export const watchlistApi = {
  getWatchlist: () => api.get('/watchlist'),
  addToWatchlist: (ratingKey) => api.post('/watchlist/add', { ratingKey }),
  removeFromWatchlist: (ratingKey) => api.post('/watchlist/remove', { ratingKey }),
}

/** Blacklist (dismissed / not-interested items) */
export const blacklistApi = {
  getBlacklist: () => api.get('/blacklist'),
  removeFromBlacklist: (ratingKey) => api.delete(`/blacklist/library/${ratingKey}`),
  removeExploreFromBlacklist: (tmdbId, mediaType) => api.delete(`/blacklist/explore/${tmdbId}/${mediaType}`),
}

/** Queue / Requests */
export const queueApi = {
  getQueue: (params = {}) => api.get('/queue', { params }),
  getRequest: (id) => api.get(`/queue/${id}`),
  createRequest: (data) => api.post('/request', data),
  approveRequest: (id) => api.post(`/queue/${id}/approve`),
  denyRequest: (id) => api.post(`/queue/${id}/deny`),
  denyRequestWithNote: (id, data) => api.post(`/queue/${id}/deny`, data),
  deleteRequest: (id) => api.delete(`/queue/${id}`),
  updateRequest: (id, data) => api.put(`/queue/${id}`, data),
  getUsers: () => api.get('/queue/users'),
  bulkDelete: (ids) => api.post('/queue/bulk-delete', { ids }),
}

/** Issues */
export const issuesApi = {
  getIssues: (params = {}) => api.get('/issues', { params }),
  getIssue: (id) => api.get(`/issues/${id}`),
  createIssue: (data) => api.post('/issues', data),
  getIssueComments: (id) => api.get(`/issues/${id}/comments`),
  addComment: (id, comment) => api.post(`/issues/${id}/comments`, { comment }),
  getUsers: () => api.get('/issues/users'),
  resolveIssue: (id) => api.post(`/issues/${id}/resolve`),
  resolveIssueWithNote: (id, data) => api.post(`/issues/${id}/resolve`, data),
  closeIssue: (id) => api.post(`/issues/${id}/close`),
  closeIssueWithNote: (id, data) => api.post(`/issues/${id}/close`, data),
  searchIssue: (id) => api.post(`/issues/${id}/search`),
  deleteIssue: (id) => api.delete(`/issues/${id}`),
  bulkDelete: (ids) => api.post('/issues/bulk-delete', { ids }),
  deleteComment: (id, commentId) => api.delete(`/issues/${id}/comments/${commentId}`),
}

/** Riven (DUMB) item actions — privileged users only */
export const rivenApi = {
  getItem: (params) => api.get('/riven/item', { params }),
  reset: (data) => api.post('/riven/reset', data),
}

/** Notifications */
export const notificationsApi = {
  getNotifications: (params = {}) => api.get('/notifications', { params }),
  markAsRead: (data) => api.post('/notifications/read', data),
  markAllAsRead: () => api.post('/notifications/read-all'),
}

/** Library server (clients, cast, dismiss, trailer) — Plex today, source-aware later */
export const libraryApi = {
  getClients: () => api.get('/clients'),
  prepareCast: (data) => api.post('/cast/prepare', data),
  castMedia: (data) => api.post('/cast', data),
  getTrailer: (tmdbId, mediaType) => api.get('/trailer', { params: { tmdbId, mediaType } }),
  dismissItem: (ratingKey) => api.post('/dismiss', { ratingKey }),
  restoreItem: (ratingKey) => api.delete('/dismiss', { data: { ratingKey } }),
}

/** Explore (external recommendations) */
export const exploreApi = {
  getServices: () => api.get('/explore/services'),
  getRecommendations: (params) => api.get('/explore/recommendations', { params }),
  dismissRecommendation: (tmdbId, mediaType) => api.post('/explore/dismiss', { tmdbId, mediaType }),
  followRecommendation: (tmdbId, mediaType) => api.post('/explore/follow', { tmdbId, mediaType }),
}

/** Tuberr (YouTube downloader) */
export const tuberrApi = {
  searchChannels: (q) => api.get('/tuberr/channels', { params: { q } }),
}

/** User Settings */
export const userApi = {
  updateSettings: (data) => api.post('/user/settings', data),
  getSettings: () => api.get('/user/settings'),
  testPushover: (userKey) => api.post('/user/pushover/test', { userKey }),
  testDiscord: (discordUserId) => api.post('/user/discord/test', { discordUserId }),
  getWebpushVapidKey: () => api.get('/user/webpush/vapid-key'),
  getWebpushSubscriptions: () => api.get('/user/webpush/subscriptions'),
  subscribeWebpush: (subscription) => api.post('/user/webpush/subscribe', { subscription }),
  unsubscribeWebpush: (endpoint) => api.post('/user/webpush/unsubscribe', { endpoint }),
  testWebpush: () => api.post('/user/webpush/test'),
  setSource: (source) => api.post('/user/source', { source }),
  getAccountLinks: () => api.get('/user/link'),
  linkJellyfin: (username, password) => api.post('/user/link/jellyfin', { username, password }),
  unlinkJellyfin: () => api.delete('/user/link/jellyfin'),
  // Only valid for a Jellyfin-identity session linked to a Plex user
  unlinkPlex: () => api.delete('/user/link/plex'),
}

/** Taste Profile (quiz) */
export const tasteApi = {
  getTaste: () => api.get('/user/taste'),
  saveTaste: (entries) => api.put('/user/taste', { entries }),
  skipQuiz: () => api.post('/user/taste/skip'),
  suggest: (type, q) => api.get('/user/taste/suggest', { params: { type, q } }),
}

/** Search interest tracking — fire-and-forget, errors deliberately swallowed */
export function searchClick(payload) {
  api.post('/search/click', payload).catch(() => { /* best-effort */ })
}

/** Poster proxy */
export const posterApi = {
  getPoster: (path) => api.get('/poster', { params: { path } }),
}

/** Admin */
export const adminApi = {
  getAdminStats: () => adminApiInstance.get('/stats'),
  syncLibrary: () => adminApiInstance.post('/sync'),
  getAdminUsers: () => adminApiInstance.get('/users'),
  promoteUser: (userId) => adminApiInstance.post(`/users/${userId}/promote`),
  demoteUser: (userId) => adminApiInstance.post(`/users/${userId}/demote`),
  updateAdminSettings: (data) => adminApiInstance.post('/settings', data),
  testConnection: (serviceName, data) => adminApiInstance.post('/test-connection', { service: serviceName, ...data }),
  getBroadcast: () => adminApiInstance.get('/broadcast'),
  setBroadcast: (message) => adminApiInstance.post('/broadcast', { message }),
  deleteBroadcast: () => adminApiInstance.delete('/broadcast'),
  rebuildPool: () => adminApiInstance.post('/admin/rebuild-pool'),
}

/** Watch History */
export const historyApi = {
  getHistory: (params) => api.get('/history', { params }),
  getUsers: () => api.get('/history/users'),
}

/** Diskovarr Wrapped */
export const wrappedApi = {
  getYears: () => api.get('/wrapped/years'),
  getWrapped: (year) => api.get(`/wrapped/${year}`),
  createPlaylist: (year) => api.post(`/wrapped/${year}/playlist`),
  recompute: (year) => api.post(`/wrapped/${year}/recompute`),
  backfill: () => api.post('/wrapped/backfill'),
}

/** Reviews */
export const reviewsApi = {
  getReviews: (params) => api.get('/reviews', { params }),
  getReview: (mediaType, tmdbId) => api.get(`/reviews/${mediaType}/${tmdbId}`),
  createReview: (data) => api.post('/reviews', data),
  updateReview: (id, data) => api.put(`/reviews/${id}`, data),
  deleteReview: (id) => api.delete(`/reviews/${id}`),
}

/** Social Reviews Feed */
export const socialReviewsApi = {
  getFeed: (params) => api.get('/reviews/feed', { params }),
  getForMedia: (mediaType, tmdbId) => api.get(`/reviews/media/${mediaType}/${tmdbId}`),
  getReview: (id) => api.get(`/reviews/${id}`),
  toggleReaction: (id) => api.post(`/reviews/${id}/react`),
  getComments: (id) => api.get(`/reviews/${id}/comments`),
  createComment: (id, data) => api.post(`/reviews/${id}/comments`, data),
  updateComment: (commentId, data) => api.put(`/reviews/comments/${commentId}`, data),
  deleteComment: (commentId) => api.delete(`/reviews/comments/${commentId}`),
}

/** Public (unauthenticated) review read — for shared links opened logged-out */
export const publicReviewsApi = {
  getReview: (id) => api.get(`/public/review/${id}`),
  getShareConfig: () => api.get('/public/share-config'),
}

/** Installed version + GitHub release notes (public) */
export const versionApi = {
  getVersion: () => api.get('/public/version'),
  getChangelog: () => api.get('/public/changelog'),
}

/** Follow System */
export const followApi = {
  initFollows: () => api.post('/users/init-follows'),
  follow: (userId) => api.post(`/users/${userId}/follow`),
  unfollow: (userId) => api.delete(`/users/${userId}/follow`),
  isFollowing: (userId) => api.get(`/users/${userId}/following`),
  getFollowers: (userId, params) => api.get(`/users/${userId}/followers`, { params }),
  getFollowing: (userId, params) => api.get(`/users/${userId}/following-list`, { params }),
}

/** User Profile */
export const profileApi = {
  getProfile: (userId) => api.get(`/users/${userId}/profile`),
  updateProfile: (data) => api.put('/users/profile', data),
  getUserReviews: (userId, params) => api.get(`/users/${userId}/reviews`, { params }),
}

/** Movie Night: groups, shared watch lists, votes, themes, rotation */
export const movieNightApi = {
  getGroups: () => api.get('/movie-night/groups'),
  createGroup: (data) => api.post('/movie-night/groups', data),
  getGroup: (id) => api.get(`/movie-night/groups/${id}`),
  updateGroup: (id, data) => api.patch(`/movie-night/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/movie-night/groups/${id}`),
  getMemberCandidates: () => api.get('/movie-night/member-candidates'),
  addMember: (id, userId) => api.post(`/movie-night/groups/${id}/members`, { userId }),
  removeMember: (id, userId) => api.delete(`/movie-night/groups/${id}/members/${userId}`),
  addPersona: (id, data) => api.post(`/movie-night/groups/${id}/personas`, data),
  updatePersona: (personaId, data) => api.patch(`/movie-night/personas/${personaId}`, data),
  deletePersona: (personaId) => api.delete(`/movie-night/personas/${personaId}`),
  getEntries: (id, params) => api.get(`/movie-night/groups/${id}/entries`, { params }),
  addEntry: (id, data) => api.post(`/movie-night/groups/${id}/entries`, data),
  updateEntry: (entryId, data) => api.patch(`/movie-night/entries/${entryId}`, data),
  markWatched: (entryId) => api.post(`/movie-night/entries/${entryId}/watched`),
  vote: (entryId, data) => api.put(`/movie-night/entries/${entryId}/vote`, data),
  getComments: (entryId) => api.get(`/movie-night/entries/${entryId}/comments`),
  addComment: (entryId, data) => api.post(`/movie-night/entries/${entryId}/comments`, data),
  deleteComment: (commentId) => api.delete(`/movie-night/comments/${commentId}`),
  getNext: (id) => api.get(`/movie-night/groups/${id}/next`),
  rotate: (id) => api.post(`/movie-night/groups/${id}/rotate`),
  getThemes: (id) => api.get(`/movie-night/groups/${id}/themes`),
  addTheme: (id, data) => api.post(`/movie-night/groups/${id}/themes`, data),
  updateTheme: (themeId, data) => api.patch(`/movie-night/themes/${themeId}`, data),
  deleteTheme: (themeId) => api.delete(`/movie-night/themes/${themeId}`),
  getNightly: () => api.get('/movie-night/nightly'),
}

/** Watch Together: synced playback across each member's own Plex player */
export const watchPartyApi = {
  getOpen: () => api.get('/watch-party'),
  create: (data) => api.post('/watch-party', data),
  get: (id) => api.get(`/watch-party/${id}`),
  invite: (id, userIds) => api.post(`/watch-party/${id}/invite`, { userIds }),
  setDevice: (id, clientId) => api.post(`/watch-party/${id}/device`, { clientId }),
  decline: (id) => api.post(`/watch-party/${id}/decline`),
  // Starting waits for every TV to load the title, well past the default timeout.
  start: (id) => api.post(`/watch-party/${id}/start`, null, { timeout: 90000 }),
  join: (id) => api.post(`/watch-party/${id}/join`, null, { timeout: 60000 }),
  control: (id, action) => api.post(`/watch-party/${id}/control`, { action }),
  end: (id) => api.post(`/watch-party/${id}/end`),
}

/** TMDB Per-User Integration */
export const tmdbApi = {
  getConnection: () => api.get('/tmdb/connection'),
  getReviews: (mediaType, tmdbId) => api.get(`/tmdb/reviews/${mediaType}/${tmdbId}`),
  initiateConnect: () => api.post('/tmdb/connect/initiate'),
  disconnect: () => api.post('/tmdb/disconnect'),
  verifySession: () => api.post('/tmdb/verify'),
  syncRating: (reviewId) => api.post('/tmdb/sync-rating', { reviewId }),
  removeRating: (reviewId) => api.delete('/tmdb/sync-rating', { data: { reviewId } }),
}

/** Error helpers */
export function isNotFoundError(e) { return e?.status === 404 }
export function isUnauthorizedError(e) { return e?.status === 401 }
export function isForbiddenError(e) { return e?.status === 403 }
export function isRateLimitError(e) { return e?.status === 429 }
export function isServerError(e) { return e?.status === 500 }

export default api
